import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { platform } from '../../api/client.js';
import LogoUpload from '../../components/platform/LogoUpload.jsx';
import {
  PageHeader, SearchBox, SortHeader, useSort, useTextFilter,
  EmptyState, ConfirmDialog, Modal, FormSection, Badge, DateTime
} from '../../components/admin/AdminUI.jsx';

/**
 * Vizora staff console — every customer, not just the signed-in one.
 *
 * Reachable only by members of the Entra platform group; the server 404s
 * anyone else, so this screen never renders for a customer administrator.
 */
export default function PlatformCustomers() {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(null);       // null | 'new' | customerId
  const [confirming, setConfirming] = useState(null);
  const [term, setTerm] = useState('');
  const { sort, toggle, sortRows } = useSort('name');

  const { data: customers = [], isLoading } = useQuery({
    queryKey: ['platform', 'customers'], queryFn: platform.listCustomers
  });
  const { data: context } = useQuery({
    queryKey: ['platform', 'context'], queryFn: platform.getContext
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['platform'] });

  const setActive = useMutation({
    mutationFn: ({ customerId, isActive }) => platform.setActive(customerId, isActive),
    onSuccess: () => { setConfirming(null); invalidate(); }
  });

  const searched = useTextFilter(customers, term, ['name', 'slug']);
  const rows = sortRows(searched, {
    name: c => c.name,
    slug: c => c.slug,
    activeUsers: c => c.activeUsers,
    issues: c => c.issues.filter(i => i.severity === 'error').length,
    lastSignInUtc: c => (c.lastSignInUtc ? new Date(c.lastSignInUtc).getTime() : null)
  });

  const withErrors = customers.filter(c => c.issues.some(i => i.severity === 'error')).length;

  return (
    <div className="admin-page">
      <PageHeader title="Customers" count={customers.length}>
        <button type="button" className="btn btn-primary btn-sm" onClick={() => setEditing('new')}>
          <i className="bi bi-plus-lg" /> New customer
        </button>
      </PageHeader>

      <p className="admin-muted small">
        Vizora staff only. Changes here affect the customer’s own portal
        immediately and are recorded in their audit log.
      </p>

      {withErrors > 0 && (
        <div className="alert alert-warning py-2 small">
          <strong>{withErrors}</strong> customer{withErrors === 1 ? '' : 's'} ha
          {withErrors === 1 ? 's' : 've'} configuration that will stop the portal
          working for their users.
        </div>
      )}

      {customers.length > 0 && (
        <div className="admin-toolbar">
          <SearchBox value={term} onChange={setTerm} placeholder="Search name or slug…" />
        </div>
      )}

      {isLoading
        ? <div className="text-center py-5"><div className="spinner-border" /></div>
        : rows.length === 0
          ? <EmptyState icon={customers.length ? 'bi-search' : 'bi-building'}
                        title={customers.length ? 'No customers match' : 'No customers yet'}>
              {customers.length ? 'Try a different search term.'
                : 'Create the first customer to get started.'}
            </EmptyState>
          : (
            <div className="admin-table-wrap">
              <table className="table table-sm admin-table mb-0">
                <thead>
                  <tr>
                    <SortHeader label="Customer" sortKey="name" sort={sort} onToggle={toggle} />
                    <SortHeader label="Tenant slug" sortKey="slug" sort={sort} onToggle={toggle} />
                    <th>Configuration</th>
                    <SortHeader label="Users" sortKey="activeUsers" sort={sort} onToggle={toggle}
                                style={{ width: 90 }} />
                    <SortHeader label="Last sign-in" sortKey="lastSignInUtc"
                                sort={sort} onToggle={toggle} />
                    <th aria-label="Actions" />
                  </tr>
                </thead>
                <tbody>
                  {rows.map(c => (
                    <tr key={c.customerId}>
                      <td>
                        <button type="button" className="btn btn-link p-0 fw-medium text-start"
                                onClick={() => setEditing(c.customerId)}>
                          {c.name}
                        </button>
                        <div className="d-flex gap-1 mt-1 flex-wrap">
                          {!c.isActive && <Badge tone="secondary">Inactive</Badge>}
                          {c.isAiEnabled && <Badge tone="primary">AI on</Badge>}
                        </div>
                      </td>
                      <td className="admin-mono">
                        {c.slug ?? <span className="admin-muted">not set</span>}
                      </td>
                      <td>
                        {c.issues.length === 0
                          ? <Badge tone="success">Healthy</Badge>
                          : <ul className="platform-issues">
                              {c.issues.map((issue, i) => (
                                <li key={i} className={`sev-${issue.severity}`}>
                                  <i className={`bi ${issue.severity === 'error'
                                    ? 'bi-x-circle-fill' : issue.severity === 'warning'
                                    ? 'bi-exclamation-triangle-fill' : 'bi-info-circle'}`} />
                                  {issue.text}
                                </li>
                              ))}
                            </ul>}
                      </td>
                      <td>{c.activeUsers}</td>
                      <td className="small text-nowrap">
                        <DateTime value={c.lastSignInUtc} />
                      </td>
                      <td className="row-actions">
                        <button type="button" className="btn btn-sm btn-link"
                                title="Edit configuration" aria-label={`Edit ${c.name}`}
                                onClick={() => setEditing(c.customerId)}>
                          <i className="bi bi-pencil" />
                        </button>
                        <button type="button"
                                className={`btn btn-sm btn-link ${c.isActive ? 'text-danger' : ''}`}
                                title={c.isActive ? 'Deactivate' : 'Reactivate'}
                                aria-label={`${c.isActive ? 'Deactivate' : 'Reactivate'} ${c.name}`}
                                onClick={() => setConfirming(c)}>
                          <i className={`bi ${c.isActive ? 'bi-pause-circle' : 'bi-play-circle'}`} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

      {editing && (
        <CustomerDialog
          customerId={editing === 'new' ? null : editing}
          context={context}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); invalidate(); }}
        />
      )}

      {confirming && (
        <ConfirmDialog
          title={confirming.isActive ? 'Deactivate this customer?' : 'Reactivate this customer?'}
          confirmLabel={confirming.isActive ? 'Deactivate' : 'Reactivate'}
          variant={confirming.isActive ? 'danger' : 'primary'}
          busy={setActive.isPending}
          onCancel={() => setConfirming(null)}
          onConfirm={() => setActive.mutate({
            customerId: confirming.customerId, isActive: !confirming.isActive
          })}
          body={confirming.isActive
            ? <>
                <p className="mb-2">
                  All <strong>{confirming.activeUsers}</strong> user(s) at{' '}
                  <strong>{confirming.name}</strong> will be unable to sign in immediately.
                </p>
                <p className="mb-0 small admin-muted">
                  Nothing is deleted — their users, profiles and saved views are kept,
                  and reactivating restores access.
                </p>
              </>
            : <p className="mb-0">
                <strong>{confirming.name}</strong> users will be able to sign in again.
              </p>}
        />
      )}
    </div>
  );
}

/* ------------------------------ dialog --------------------------------- */

const BLANK = {
  name: '', slug: '', powerBiWorkspaceId: '',
  entraTenantId: '', allowedEmailDomains: '', isActive: true, isAiEnabled: false,
  /* A string, not a number, and blank rather than 0. Empty means "no ceiling";
     a typed 0 means "this customer may not spend". Initialising to 0 would make
     those two indistinguishable in the form state, and the wrong one is the
     default. */
  aiSpendCapAmount: '',
  domains: []
};

function CustomerDialog({ customerId, context, onClose, onSaved }) {
  const isNew = !customerId;
  const { data: existing } = useQuery({
    queryKey: ['platform', 'customer', customerId],
    queryFn: () => platform.getCustomer(customerId),
    enabled: !isNew
  });

  const [form, setForm] = useState(BLANK);
  const [loaded, setLoaded] = useState(isNew);
  const [error, setError] = useState(null);

  if (existing && !loaded) {
    setForm({
      name: existing.name ?? '',
      slug: existing.slug ?? '',
      powerBiWorkspaceId: existing.powerBiWorkspaceId ?? '',
      entraTenantId: existing.entraTenantId ?? '',
      allowedEmailDomains: existing.allowedEmailDomains ?? '',
      isActive: existing.isActive,
      isAiEnabled: existing.isAiEnabled,
      aiSpendCapAmount: existing.aiSpendCapAmount === null
        || existing.aiSpendCapAmount === undefined
        ? '' : String(existing.aiSpendCapAmount),
      domains: existing.domains ?? []
    });
    setLoaded(true);
  }

  const save = useMutation({
    mutationFn: (payload) => isNew
      ? platform.createCustomer(payload)
      : platform.updateCustomer(customerId, payload),
    onSuccess: onSaved,
    onError: (err) => setError(
      err.body?.issues?.map(i => `${i.path}: ${i.message}`).join('; ')
      ?? err.body?.error ?? err.message)
  });

  const set = (patch) => setForm(f => ({ ...f, ...patch }));

  const submit = (e) => {
    e.preventDefault();
    setError(null);
    save.mutate({
      ...form,
      slug: form.slug || null,
      powerBiWorkspaceId: form.powerBiWorkspaceId || null,
      entraTenantId: form.entraTenantId || null,
      allowedEmailDomains: form.allowedEmailDomains.trim() || null,
      /* Sent as the RAW STRING, not Number(). This is a fix for a defect that
         would have been very hard to spot: Number('1,000') is NaN,
         JSON.stringify turns NaN into null, and null means "no ceiling" — so
         typing a thousand with a comma would have reported success while
         deleting the ceiling. The server parses it strictly and refuses
         anything it cannot read. Empty still means no ceiling. */
      aiSpendCapAmount: String(form.aiSpendCapAmount).trim() === ''
        ? null : String(form.aiSpendCapAmount).trim(),
      domains: form.domains.filter(d => d.hostName.trim())
    });
  };

  const setPrimary = (index) => set({
    domains: form.domains.map((d, i) => ({ ...d, isPrimary: i === index }))
  });

  return (
    <Modal
      title={isNew ? 'New customer' : `Configure ${existing?.name ?? ''}`}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-sm btn-outline-secondary" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" form="customer-form" className="btn btn-sm btn-primary"
                  disabled={save.isPending}>
            {save.isPending ? 'Saving…' : (isNew ? 'Create customer' : 'Save changes')}
          </button>
        </>
      }
    >
      <form id="customer-form" className="modal-body" onSubmit={submit}>
        {error && <div className="alert alert-danger py-2 small">{error}</div>}

        <FormSection title="Identity">
          <div className="row g-3">
            <div className="col-md-6">
              <label className="form-label" htmlFor="cname">Customer name</label>
              <input id="cname" className="form-control form-control-sm" required autoFocus
                     value={form.name} onChange={e => set({ name: e.target.value })} />
              <div className="form-text">Shown in the sidebar when there is no logo.</div>
            </div>
            <div className="col-md-6">
              <label className="form-label" htmlFor="cslug">Tenant slug</label>
              <input id="cslug" className="form-control form-control-sm admin-mono"
                     value={form.slug} placeholder="toppesfield"
                     onChange={e => set({ slug: e.target.value.toLowerCase() })} />
              <div className="form-text">
                Sent to the AI backend as the <code>tenant</code> claim. Must match the
                tenant the AI workstream registered, or the assistant returns 403.
              </div>
            </div>
            <div className="col-md-6 d-flex align-items-center gap-4 pt-2">
              <div className="form-check">
                <input id="cactive" className="form-check-input" type="checkbox"
                       checked={form.isActive}
                       onChange={e => set({ isActive: e.target.checked })} />
                <label className="form-check-label" htmlFor="cactive">Active</label>
              </div>
            </div>
          </div>
        </FormSection>

        <FormSection title="Hostnames"
                     hint="How users reach this customer. The primary hostname decides the redirect URI used at sign-in, so it must also be registered on the Entra app registration.">
          {form.domains.map((d, i) => (
            <div className="d-flex gap-2 align-items-center mb-2" key={i}>
              <input className="form-control form-control-sm admin-mono"
                     value={d.hostName} placeholder="customer.vizora.co.uk"
                     aria-label="Hostname"
                     onChange={e => set({
                       domains: form.domains.map((x, j) =>
                         j === i ? { ...x, hostName: e.target.value } : x)
                     })} />
              <div className="form-check flex-shrink-0 m-0">
                <input className="form-check-input" type="radio" id={`primary${i}`}
                       name="primaryHost" checked={!!d.isPrimary}
                       onChange={() => setPrimary(i)} />
                <label className="form-check-label small" htmlFor={`primary${i}`}>Primary</label>
              </div>
              <button type="button" className="btn btn-sm btn-link text-danger flex-shrink-0"
                      aria-label="Remove hostname"
                      onClick={() => set({ domains: form.domains.filter((_, j) => j !== i) })}>
                <i className="bi bi-trash" />
              </button>
            </div>
          ))}
          <button type="button" className="btn btn-sm btn-outline-primary"
                  onClick={() => set({
                    domains: [...form.domains,
                      { hostName: '', isPrimary: form.domains.length === 0 }]
                  })}>
            <i className="bi bi-plus-lg" /> Add hostname
          </button>
        </FormSection>

        <FormSection title="Power BI">
          <div className="row g-3">
            <div className="col-md-8">
              <label className="form-label" htmlFor="cws">Workspace id</label>
              <input id="cws" className="form-control form-control-sm admin-mono"
                     value={form.powerBiWorkspaceId}
                     placeholder="00000000-0000-0000-0000-000000000000"
                     onChange={e => set({ powerBiWorkspaceId: e.target.value.trim() })} />
              <div className="form-text">
                The GUID in the Power BI URL: <code>app.powerbi.com/groups/&lt;id&gt;/…</code>.
                Reports only appear after an admin syncs the catalogue.
              </div>
            </div>
          </div>
        </FormSection>

        <FormSection title="Logos">
          {/* Uploaded, not named. These were three text boxes holding blob
              names until 016; nobody could see from this screen whether the
              file existed or what it looked like. */}
          <LogoUpload customerId={existing?.customerId ?? null} />
        </FormSection>

        <FormSection title="AI assistant">
          <div className="form-check mb-2">
            <input id="cai" className="form-check-input" type="checkbox"
                   checked={form.isAiEnabled}
                   onChange={e => set({ isAiEnabled: e.target.checked })} />
            <label className="form-check-label" htmlFor="cai">
              Show the Vizora AI Data Assistant to this customer
            </label>
          </div>
          {form.isAiEnabled && !form.slug && (
            <div className="alert alert-warning py-2 small mb-2">
              A tenant slug is required — without it the AI backend cannot identify
              this customer and every question returns 403.
            </div>
          )}

          {form.isAiEnabled && (
            <div className="mb-3">
              <label className="form-label" htmlFor="ccap">
                Monthly spend ceiling
              </label>
              <input id="ccap" className="form-control form-control-sm"
                     style={{ maxWidth: 200 }} inputMode="decimal"
                     placeholder="No ceiling"
                     value={form.aiSpendCapAmount}
                     onChange={e => set({ aiSpendCapAmount: e.target.value })} />
              <div className="form-text">
                The most this customer may be charged for the assistant in a UTC
                calendar month, at Vizora&rsquo;s current rates. When reached, the
                assistant stops for every user in that organisation until the 1st.
                Their own administrator may set a <em>lower</em> cap on their
                Assistant Spend screen but can never exceed this one.
                <br />
                Leave empty for no ceiling. <strong>0 is a real ceiling</strong> —
                it means this customer may not spend anything, which is not the
                same as leaving the box blank.
                {existing?.aiSpendCapCustomerAmount !== null
                  && existing?.aiSpendCapCustomerAmount !== undefined && (
                  <><br />This customer has set their own cap of{' '}
                    {existing.aiSpendCapCustomerAmount}
                    {existing.aiSpendCapNotifyEmail
                      ? <>, notifying <code>{existing.aiSpendCapNotifyEmail}</code>.</>
                      : ', with no notification address.'}
                  </>
                )}
              </div>
            </div>
          )}

          <p className="form-text mb-0">
            Model choice, pricing, semantic models and profile→role mapping are owned
            by the AI service, not the portal
            {context?.aiAdminStudioUrl
              ? <> — manage those in the{' '}
                  <a href={context.aiAdminStudioUrl} target="_blank" rel="noopener noreferrer">
                    AI Admin Studio <i className="bi bi-box-arrow-up-right" />
                  </a>.</>
              : <>. Set <code>AI_ADMIN_STUDIO_URL</code> to link to it from here.</>}
          </p>
        </FormSection>

        <FormSection title="Entra"
                     hint="Optional, and usually left blank. When set, the signer's home tenant must match. Guests invited from this customer's own directory satisfy it; anyone else is refused. Leave empty unless you know every user of this customer signs in from that one tenant — a single consultant on a personal account would be locked out.">
          <div className="col-md-12 mb-3">
            <label className="form-label" htmlFor="cdomains">Allowed email domains</label>
            <input id="cdomains" className="form-control form-control-sm admin-mono"
                   placeholder="customer.com, customer.co.uk"
                   value={form.allowedEmailDomains}
                   onChange={e => set({ allowedEmailDomains: e.target.value })} />
            <div className="form-text">
              Leave blank for no check. When set, a user cannot be created — or
              sign in — unless their address is in one of these domains, which
              catches someone from another company being added to this customer
              by mistake. Subdomains are included, so <code>customer.com</code>{' '}
              also admits <code>@mail.customer.com</code>. Vizora support
              sessions are exempt.
            </div>
          </div>

          <div className="col-md-8">
            <label className="form-label" htmlFor="ctenant">Home tenant id</label>
            <input id="ctenant" className="form-control form-control-sm admin-mono"
                   value={form.entraTenantId}
                   onChange={e => set({ entraTenantId: e.target.value.trim() })} />
          </div>
        </FormSection>
      </form>
    </Modal>
  );
}
